import { useEffect, useRef, useState } from "react";
import { useChatStore } from "@/store/chat";
import { useChatIdentity } from "../api/use-session";
import type { Session } from "../types";

const demoMembers: Record<string, string> = {
  alice: "990001",
  bob: "990002",
};

function requestedIdentity() {
  if (
    import.meta.env.VITE_WEBCHAT_DEMO_LOGIN !== "true" ||
    !import.meta.env.DEV ||
    !["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)
  )
    return null;
  return (
    demoMembers[
      new URL(window.location.href).searchParams.get("chatDemo") ?? ""
    ] ?? null
  );
}

/** Active only in builds with VITE_WEBCHAT_DEMO_LOGIN=true. Local test links use the existing signed fixture session, never a URL identity on chat commands. */
export function useDemoLogin(session: Session | undefined) {
  const [requested] = useState(requestedIdentity);
  const attempted = useRef(false);
  const login = useChatIdentity();
  const target =
    session?.mode === "local-demo" &&
    session.identities.some((item) => item.memberId === requested)
      ? requested
      : null;
  const { mutate, isIdle, isSuccess, error, reset } = login;

  useEffect(() => {
    if (!target || !isIdle || attempted.current) return;
    attempted.current = true;
    mutate(target, {
      onSuccess: () => {
        useChatStore.getState().clearPrivate();
        useChatStore.getState().setOpen(true);
        const url = new URL(window.location.href);
        url.searchParams.delete("chatDemo");
        window.history.replaceState(window.history.state, "", url);
      },
    });
  }, [target, isIdle, mutate]);

  return {
    isPending:
      Boolean(requested && !session) || Boolean(target && !isSuccess && !error),
    error,
    retry: () => {
      attempted.current = false;
      reset();
    },
  };
}
